/**
 * wayai report — manage your own platform reports.
 *
 * Subcommands:
 *   wayai report create <--title|-t> <--description|-d> [--error|-e] [--steps|-s] [--context] ...
 *   wayai report edit <id> [--title|-t] [--description|-d] [--error|-e]
 *                          [--steps|-s] [--context]
 *
 * `create` submits a new report to the triage queue. `edit` amends the mutable
 * content of a report you filed via `wayai report create`
 * (title/description/error/steps/context), provided it is still `pending` (not
 * yet triaged). Editing never changes the dedup fingerprint and never re-emails
 * you. Once a report has been escalated/grouped/resolved, only a platform admin
 * can amend it — see `wayai admin report update`.
 */

import { requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { parseReportEditArgs } from '../lib/report-edit-args.js';
import { reportCreateCommand } from './report-create.js';

export async function reportCommand(args: string[]): Promise<void> {
  const sub = args[0];
  switch (sub) {
    case 'create':
      await reportCreateCommand(args.slice(1));
      return;
    case 'edit':
      await reportEditCommand(args.slice(1));
      return;
    case 'list':
      await reportListCommand(args.slice(1));
      return;
    case 'get':
      await reportGetCommand(args.slice(1));
      return;
    case 'accept':
      await reportAcceptCommand(args.slice(1));
      return;
    case 'contest':
      await reportContestCommand(args.slice(1));
      return;
    default:
      printReportHelp();
      process.exit(1);
  }
}

function printReportHelp(): void {
  console.error(`
wayai report — manage your own platform reports

Usage:
  wayai report create <OPTIONS>            Submit a new report to the triage queue
  wayai report edit <report_id> <OPTIONS>  Amend your own pending report
  wayai report list [--status <s>] [--json]   List your reports (newest first)
  wayai report get <report_id> [--json]    Show a report's status + message thread
  wayai report accept <report_id>          Accept a shipped fix (→ addressed)
  wayai report contest <report_id> --reason <text>
                                           Contest a shipped fix or a dismissal (→ triage)

Create options (--title and --description required):
  -t, --title <text>         Bug title
  -d, --description <text>   Detailed description
  -e, --error <text>         Error message or stack trace
  -s, --steps <text>         Steps to reproduce
      --context <text>       Additional context
  (run \`wayai report create\` with no args for the full option list)

Edit options (at least one required):
  -t, --title <text>         New title
  -d, --description <text>   New description
  -e, --error <text>         New error message / stack trace ("" clears it)
  -s, --steps <text>         New steps to reproduce ("" clears them)
      --context <text>       New additional context ("" clears it)

The verification loop:
  After triage escalates and the fix ships, your report moves to 'shipped'. Poll
  \`wayai report list --status shipped\` to find it, then \`accept\` if the fix works or
  \`contest --reason "..."\` if it doesn't. You can also \`contest\` a 'dismissed' report
  you believe is real — it goes back to triage. Contests are bounded (a cap, and a
  triage 'final' dismissal); past those, contact support.

Notes:
  Only your own \`wayai report create\` submissions can be read/acted on. Editing is
  allowed only while 'pending'; the dedup fingerprint stays frozen and no email is
  sent on an edit.
`.trim());
}

async function reportEditCommand(args: string[]): Promise<void> {
  const { reportId, patch, error } = parseReportEditArgs(args);

  if (error) {
    console.error(error);
    process.exit(1);
  }
  if (!reportId || Object.keys(patch).length === 0) {
    console.error('Usage: wayai report edit <report_id> [--title|-t] [--description|-d] [--error|-e] [--steps|-s] [--context]');
    console.error('At least one field to change is required. Pass "" to clear error/steps/context.');
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  try {
    const result = await client.editReport(reportId, patch);
    console.log(`Report updated: ${result.report_id} (status: ${result.status})`);
    console.log('The dedup fingerprint is unchanged and no notification was sent.');
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 400) {
        console.error(`Invalid edit: ${err.message}`);
      } else if (err.status === 404) {
        console.error('Report not found, or it was not filed by you.');
      } else if (err.status === 403) {
        console.error('This report is agent-authored and can only be edited by a platform admin.');
      } else if (err.status === 409) {
        console.error(`Cannot edit: ${err.message}`);
      } else if (err.status === 429) {
        console.error('Rate limit exceeded. Please try again later.');
      } else {
        console.error(`Failed to edit report: ${err.message}`);
      }
      process.exit(1);
    }
    throw err;
  }
}

async function reportListCommand(args: string[]): Promise<void> {
  const status = flagValue(args, '--status');
  const json = args.includes('--json');
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.listMyReports(status);
    if (json) {
      console.log(JSON.stringify(result, null, 2));
      return;
    }
    if (result.data.length === 0) {
      console.log('No reports found.');
      return;
    }
    for (const r of result.data) {
      console.log(`${r.report_id}  ${r.status.padEnd(10)}  ${r.classification}`);
    }
  } catch (err) {
    reportActionError(err);
  }
}

async function reportGetCommand(args: string[]): Promise<void> {
  const reportId = args.find((a) => !a.startsWith('--'));
  const json = args.includes('--json');
  if (!reportId) {
    console.error('Usage: wayai report get <report_id> [--json]');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.getMyReport(reportId);
    if (json) {
      console.log(JSON.stringify(result, null, 2));
      return;
    }
    const { report, messages } = result;
    console.log(`Report ${report.report_id}`);
    console.log(`  status:         ${report.status}`);
    console.log(`  classification: ${report.classification}`);
    if (report.contest_count > 0) console.log(`  contests:       ${report.contest_count}`);
    if (report.dismissal_final) console.log('  dismissal:      final (non-contestable)');
    if (messages.length > 0) {
      console.log('\n  Thread:');
      for (const m of messages) {
        console.log(`    [${m.author_role}] ${m.body}`);
      }
    }
    if (report.status === 'shipped') {
      console.log(`\n  This fix is shipped. Run \`wayai report accept ${report.report_id}\` if it works,`);
      console.log(`  or \`wayai report contest ${report.report_id} --reason "..."\` if it does not.`);
    }
  } catch (err) {
    reportActionError(err);
  }
}

async function reportAcceptCommand(args: string[]): Promise<void> {
  const reportId = args.find((a) => !a.startsWith('--'));
  if (!reportId) {
    console.error('Usage: wayai report accept <report_id>');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.acceptReport(reportId);
    console.log(`Accepted. Report ${result.report.report_id} → ${result.report.status}.`);
  } catch (err) {
    reportActionError(err);
  }
}

async function reportContestCommand(args: string[]): Promise<void> {
  const reportId = args.find((a) => !a.startsWith('--'));
  const reason = flagValue(args, '--reason');
  if (!reportId || !reason) {
    console.error('Usage: wayai report contest <report_id> --reason "<why the fix/dismissal is wrong>"');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.contestReport(reportId, reason);
    console.log(`Contested. Report ${result.report.report_id} → ${result.report.status} (back to triage).`);
  } catch (err) {
    reportActionError(err);
  }
}

// Read the value following `flag` in argv (e.g. `--status shipped`).
function flagValue(args: string[], flag: string): string | undefined {
  const i = args.indexOf(flag);
  return i >= 0 && i + 1 < args.length ? args[i + 1] : undefined;
}

// Shared ApiError → friendly message + exit for the reporter verbs (get/list/accept/contest).
function reportActionError(err: unknown): never {
  if (err instanceof ApiError) {
    if (err.status === 404) {
      console.error('Report not found, or it was not filed by you.');
    } else if (err.status === 403) {
      console.error('This report is agent-authored and can only be handled by a platform admin.');
    } else if (err.status === 409) {
      // Conflict carries the actionable reason (wrong status, final dismissal, cap reached).
      console.error(err.message);
    } else if (err.status === 429) {
      console.error('Rate limit exceeded. Please try again later.');
    } else {
      console.error(`Request failed: ${err.message}`);
    }
    process.exit(1);
  }
  throw err;
}
